import { useState, type ChangeEvent, type FormEvent } from 'react';

import type { PortalCase, PortalDocument, PortalReport } from '../lib/api.ts';
import { documentDownloadHref, portalApi, reportDownloadHref } from '../lib/api.ts';
import { formatDate, formatDateTime, formatFileSize, humanize, statusTone } from '../lib/format.ts';
import { describeError, type ErrorInfo } from '../lib/request-id.ts';
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Notice,
  Table,
  Td,
  Th,
} from './ui.tsx';

interface Props {
  caseRecord: PortalCase;
  documents: PortalDocument[];
  reports: PortalReport[];
  /** Called after a successful upload so the parent can refresh the case detail. */
  onUploaded?: () => void;
}

/**
 * The client upload control (T-7.11; US-101). Rendered ONLY when the firm has enabled
 * portal uploads (`caseRecord.uploads_enabled`) — otherwise the case view stays read-only.
 * Posts the picked file to the client's OWN case via the feature-gated portal upload route;
 * on success it clears the picker and asks the parent to refresh the document list. Errors
 * surface as the typed, non-revealing message (a 403 disabled-firm / 404 not-your-case both
 * read as a friendly line — the SPA never sees a stack).
 */
function UploadControl({
  caseNumber,
  onUploaded,
}: {
  caseNumber: string;
  onUploaded?: (() => void) | undefined;
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorInfo | null>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);
  // Bumped after a successful upload so the (uncontrolled) file picker actually clears.
  const [pickerKey, setPickerKey] = useState(0);

  const onPick = (e: ChangeEvent<HTMLInputElement>): void => {
    setFile(e.target.files?.[0] ?? null);
    setError(null);
    setUploaded(null);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    setUploaded(null);
    void (async () => {
      try {
        const doc = await portalApi.uploadDocument(caseNumber, file);
        setUploaded(doc.filename);
        setFile(null);
        setPickerKey((k) => k + 1);
        onUploaded?.();
      } catch (err) {
        setError(describeError(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <form className="portal-upload" aria-label="Document upload" onSubmit={onSubmit}>
      <div className="portal-upload__row">
        <Input
          key={pickerKey}
          id="portal-upload-file"
          type="file"
          className="portal-file-input"
          label="Upload a document to this case"
          help="One file at a time. Your firm sees it as soon as the upload completes."
          onChange={onPick}
          disabled={busy}
        />
        <Button type="submit" variant="primary" loading={busy} disabled={!file || busy}>
          {busy ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
      {busy ? (
        <div className="portal-progress" aria-hidden="true">
          <div className="portal-progress__bar" />
        </div>
      ) : null}
      {error ? (
        <ErrorState title="Upload failed" message={error.message} requestId={error.requestId} />
      ) : null}
      {uploaded ? <Notice tone="success">{`Uploaded ${uploaded}.`}</Notice> : null}
    </form>
  );
}

/**
 * A single case's view as a column of cards: the case (status, period), the upload card
 * (ONLY when the firm enabled portal uploads), the SHARED documents table (each document
 * offering a download — F-35) and the SHARED (completed) reports table, each report offering
 * a download link per rendered format. Every download is a plain `<a href>` at the portal
 * download route (302 → signed R2 URL, audited server-side); the SPA never handles the token
 * or the bytes. Only what the backend lists is downloadable — the document list already
 * excludes unfinalized and deleted files, so there is no client-side gating. Every other
 * view is read-only.
 */
export function CaseDetail({ caseRecord, documents, reports, onUploaded }: Props): JSX.Element {
  return (
    <section className="stack" aria-label={`Case ${caseRecord.case_number}`}>
      <Card>
        <CardHeader
          title={caseRecord.title}
          description={<span className="mono">{caseRecord.case_number}</span>}
          actions={
            <Badge tone={statusTone(caseRecord.status)}>{humanize(caseRecord.status)}</Badge>
          }
        />
        <CardBody>
          <dl className="portal-facts">
            <div>
              <dt>Engagement</dt>
              <dd>{humanize(caseRecord.engagement_type)}</dd>
            </div>
            <div>
              <dt>Assessment period</dt>
              <dd>
                {formatDate(caseRecord.period_start)} – {formatDate(caseRecord.period_end)}
              </dd>
            </div>
            <div>
              <dt>Last updated</dt>
              <dd>{formatDate(caseRecord.updated_at)}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      {caseRecord.uploads_enabled ? (
        <Card>
          <CardHeader
            title="Send a document"
            description="Your firm accepts documents from you on this case."
          />
          <CardBody>
            <UploadControl caseNumber={caseRecord.case_number} onUploaded={onUploaded} />
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader
          title="Shared documents"
          description={documents.length === 1 ? '1 document' : `${documents.length} documents`}
        />
        <CardBody flush>
          {documents.length === 0 ? (
            <EmptyState
              compact
              title="No documents shared"
              description="Files your firm makes available to you appear here."
            />
          ) : (
            <Table caption="Shared documents">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th numeric>Size</Th>
                  <Th numeric>Pages</Th>
                  <Th shrink>Shared on</Th>
                  <Th shrink>Download</Th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => (
                  <tr key={d.id}>
                    <Td>{d.filename}</Td>
                    <Td>{d.doc_type ? humanize(d.doc_type) : 'Document'}</Td>
                    <Td numeric>{formatFileSize(d.size_bytes)}</Td>
                    <Td numeric>{d.page_count !== null ? d.page_count : '—'}</Td>
                    <Td shrink className="text-muted">
                      {formatDateTime(d.uploaded_at)}
                    </Td>
                    <Td shrink>
                      <ButtonLink
                        size="sm"
                        variant="secondary"
                        href={documentDownloadHref(caseRecord.case_number, d.id)}
                        rel="noopener"
                        aria-label={`Download ${d.filename}`}
                      >
                        Download
                      </ButtonLink>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Shared reports"
          description={reports.length === 1 ? '1 report' : `${reports.length} reports`}
        />
        <CardBody flush>
          {reports.length === 0 ? (
            <EmptyState
              compact
              title="No reports shared yet"
              description="Completed reports your firm releases appear here with a download for each format."
            />
          ) : (
            <Table caption="Shared reports">
              <thead>
                <tr>
                  <Th>Report</Th>
                  <Th>Type</Th>
                  <Th numeric>Version</Th>
                  <Th shrink>Generated</Th>
                  <Th shrink>Download</Th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id}>
                    <Td>
                      <span className="row">
                        <span>{r.title}</span>
                        {r.is_court_bundle ? <Badge tone="accent">Court bundle</Badge> : null}
                      </span>
                    </Td>
                    <Td>{humanize(r.report_type)}</Td>
                    <Td numeric>v{r.version}</Td>
                    <Td shrink className="text-muted">
                      {formatDateTime(r.generated_at)}
                    </Td>
                    <Td shrink>
                      {r.available_formats.length === 0 ? (
                        <span className="text-muted">Preparing…</span>
                      ) : (
                        <span className="portal-formats">
                          {r.available_formats.map((fmt) => (
                            <ButtonLink
                              key={fmt}
                              size="sm"
                              variant="secondary"
                              href={reportDownloadHref(caseRecord.case_number, r.id, fmt)}
                              rel="noopener"
                            >
                              {fmt.toUpperCase()}
                            </ButtonLink>
                          ))}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </section>
  );
}
